import React, { useState, useEffect } from 'react';
import Sidebar from './../components/sidebar';
import Button from "react-bootstrap/Button";
import { Container, Row, Col, Card, Table, ProgressBar, Spinner } from 'react-bootstrap';
import { FaUsers, FaCoins, FaEnvelope, FaChartBar, FaShoppingCart } from 'react-icons/fa';
import * as XLSX from 'xlsx';

const Reportes = () => {
  const [reportData, setReportData] = useState({
    totals: {
      referidos: 0,
      ganancias: 0,
      ganancias25: 0, // Nuevo campo para el 25%
      invitaciones: 0,
      efectivas: 0,
      compradores: 0,
      totalVentas: 0
    },
    invitados: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Función para obtener pedidos de WooCommerce
  const fetchWooCommerceOrders = async () => {
    try {
      const response = await fetch('https://mrcirp.com/wp-json/wc/v3/pedidos-publicos/?status=completed');
      return await response.json();
    } catch (error) {
      console.error('Error fetching WooCommerce orders:', error);
      return [];
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Obtener datos de referidos
        const referidosResponse = await fetch('https://api.mrcirp.com/public/reportes');
        const referidosData = await referidosResponse.json();
        
        // Obtener pedidos de WooCommerce
        const orders = await fetchWooCommerceOrders();
        
        // Cruzar datos para encontrar compras de invitados
        const invitadosConCompras = referidosData.map((item, index) => {
          const compras = orders.filter(order => order.cliente_email === item.correo);
          const totalComprado = compras.reduce((sum, order) => sum + parseFloat(order.total), 0);
          
          return {
            id: item.id || index + 1,
            nombre: item.nombre || 'Sin nombre',
            email: item.correo || 'Sin correo',
            programa: item.producto || 'Sin programa',
            amigo_nombre: item.amigo_nombre || 'No especificado',
            amigo_email: item.amigo_email || 'No especificado',
            estado: item.estado || 'desconocido',
            fecha: item.fecha || 'No especificada',
            haComprado: compras.length > 0,
            compras: compras,
            totalComprado: totalComprado,
            ganancia25: totalComprado * 0.25 // Calculamos el 25% para cada invitado
          };
        });

        // Calcular métricas
        const compradores = invitadosConCompras.filter(i => i.haComprado).length;
        const totalVentas = invitadosConCompras.reduce((sum, item) => sum + item.totalComprado, 0);
        const ganancias25 = totalVentas * 0.25; // Calculamos el 25% del total de ventas

        setReportData({
          totals: {
            referidos: referidosData.length,
            ganancias: referidosData.reduce((sum, item) => sum + (item.ganancia || 0), 0),
            ganancias25: ganancias25, // Agregamos el 25% calculado
            invitaciones: referidosData.length,
            efectivas: referidosData.filter(item => item.estado === 'activo').length,
            compradores: compradores,
            totalVentas: totalVentas
          },
          invitados: invitadosConCompras
        });
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Error al cargar los datos. Por favor intenta nuevamente.');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calcular porcentajes
  const efectividad = reportData.totals.invitaciones > 0 
    ? (reportData.totals.efectivas / reportData.totals.invitaciones) * 100 
    : 0;

  const conversion = reportData.totals.invitaciones > 0
    ? (reportData.totals.compradores / reportData.totals.invitaciones) * 100
    : 0;

  // Exportar a Excel
  const exportToExcel = () => {
    const excelData = reportData.invitados.map(invitado => ({
      '#': invitado.id,
      'Nombre': invitado.nombre,
      'Email': invitado.email,
      'Programa': invitado.programa,
      'Amigo Referido': invitado.amigo_nombre,
      'Email Amigo': invitado.amigo_email,
      'Estado': invitado.estado === 'activo' ? 'Activo' : 'Inactivo',
      'Ha comprado': invitado.haComprado ? 'Sí' : 'No',
      'Total comprado': invitado.haComprado ? `$${invitado.totalComprado.toFixed(2)}` : '$0.00',
      '25% Ganancias': invitado.haComprado ? `$${invitado.ganancia25.toFixed(2)}` : '$0.00',
      'N° Compras': invitado.compras.length
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Invitados");
    XLSX.writeFile(wb, `Reporte_Invitados_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (loading) {
    return (
      <Sidebar>
        <Container className="mt-4 mb-5">
          <Row className="justify-content-center">
            <Col md="auto">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Cargando reportes...</p>
            </Col>
          </Row>
        </Container>
      </Sidebar>
    );
  }

  if (error) {
    return (
      <Sidebar>
        <Container className="mt-4 mb-5">
          <Row className="justify-content-center">
            <Col md="auto">
              <div className="alert alert-danger">
                {error}
              </div>
              <Button variant="primary" onClick={() => window.location.reload()}>
                Reintentar
              </Button>
            </Col>
          </Row>
        </Container>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <Container className="mt-4 mb-5">
        <Row className="mb-4">
          <Col>
            <h3><FaChartBar className="me-2" />Reportes de Referidos</h3>
            <p className="text-muted">Resumen completo de tu actividad en el programa de referidos en la plataforma</p>
          </Col>
        </Row>

        {/* Tarjetas resumen */}
        <Row className="mb-4">
          <Col md={3} className="mb-3">
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-2">Clientes Referidos</h6>
                    <h3>{reportData.totals.referidos}</h3>
                  </div>
                  <div className="bg-primary bg-opacity-10 p-3 rounded">
                    <FaUsers size={24} className="text-primary" />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} className="mb-3">
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-2">25% de Ganancias</h6>
                    <h3>${reportData.totals.ganancias25.toFixed(2)}</h3>
                    <small className="text-muted">De ${reportData.totals.totalVentas.toFixed(2)} en ventas</small>
                  </div>
                  <div className="bg-success bg-opacity-10 p-3 rounded">
                    <FaCoins size={24} className="text-success" />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} className="mb-3">
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-2">Compraron</h6>
                    <h3>{reportData.totals.compradores}</h3>
                    <small>{conversion.toFixed(1)}% de conversión</small>
                  </div>
                  <div className="bg-info bg-opacity-10 p-3 rounded">
                    <FaShoppingCart size={24} className="text-info" />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} className="mb-3">
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-2">Total Ventas</h6>
                    <h3>${reportData.totals.totalVentas.toFixed(2)}</h3>
                  </div>
                  <div className="bg-warning bg-opacity-10 p-3 rounded">
                    <FaChartBar size={24} className="text-warning" />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Listado de invitados */}
        <Row>
          <Col>
            <Card className="shadow-sm">
              <Card.Body>
                <Card.Title className="mb-4">Base de Datos de Invitados</Card.Title>
                
                {reportData.invitados.length > 0 ? (
                  <div className="table-responsive">
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Nombre</th>
                          <th>Email</th>
                          <th>Programa</th>
                          <th>Estado</th>
                          <th>Compras</th>
                          <th>Total Comprado</th>
                          <th>25% Ganancias</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.invitados.map((invitado) => (
                          <tr key={invitado.id}>
                            <td>{invitado.id}</td>
                            <td>{invitado.nombre}</td>
                            <td>{invitado.email}</td>
                            <td>
                              {invitado.programa === 'KATANA' 
                                ? <span className="badge bg-primary">{invitado.programa}</span>
                                : <span className="badge bg-success">{invitado.programa}</span>}
                            </td>
                            <td>
                              <span className={`badge ${invitado.estado === 'activo' ? 'bg-success' : 'bg-secondary'}`}>
                                {invitado.estado === 'activo' ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                            <td>
                              {invitado.haComprado ? (
                                <span className="badge bg-success">
                                  {invitado.compras.length} compra(s)
                                </span>
                              ) : (
                                <span className="badge bg-secondary">No ha comprado</span>
                              )}
                            </td>
                            <td>
                              {invitado.haComprado ? (
                                <span className="text-success fw-bold">
                                  ${invitado.totalComprado.toFixed(2)}
                                </span>
                              ) : '$0.00'}
                            </td>
                            <td>
                              {invitado.haComprado ? (
                                <span className="text-primary fw-bold">
                                  ${invitado.ganancia25.toFixed(2)}
                                </span>
                              ) : '$0.00'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p>No hay invitados registrados aún.</p>
                  </div>
                )}

                <div className="d-flex justify-content-end mt-3">
                  <Button 
                    variant="outline-primary" 
                    onClick={exportToExcel}
                    disabled={reportData.invitados.length === 0}
                  >
                    Exportar a Excel
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </Sidebar>
  );
};

export default Reportes;